import React, { useEffect, useReducer, useState } from 'react';
import {
  AutocompleteComponent,
  DialogComponent,
  Inputs,
  Spinner,
} from '../../../../../../Components';
import { useVendorContext } from '../../../context/VendorContext';
import LookupAutocomplete from '../../../../../../Components/Controls/LookupAutocomplete/LookupAutocomplete';
import Joi from 'joi';
import { useTranslation } from 'react-i18next';
import {
  getErrorByName,
  showError,
  showSuccess,
} from '../../../../../../Helper';
import {
  CreateVendorService,
  UpdateVendorService,
} from '../../../../../../Services';
import { useKeyGenerator } from '../../../../../../Hooks/keyGenerator';
import { mapActiveItem, mapStateToSaveDto } from '../model';
const reducer = (state, action) => {
  if (action.id !== 'edit') return { ...state, [action.id]: action.value };
  return {
    ...action.value,
  };
};
const ServiceForm = ({
  parentTranslationPath,
  serviceSettingId,
  activeItem,
  reload,
  onClose,
  open,
}) => {
  const initialState = {
    serviceType: null,
    serviceAvailabilityId: null,
    description: null,
    vendorServiceCategoryId: null,
    serviceSettingId,
    selectedAvailability: {},
    selectedCategory: {},
    selectedTags: [],
    vendorServiceTagIds: [],
  };
  const { t } = useTranslation(parentTranslationPath);
  const { categories, refetchCategories } = useVendorContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [key, refresh] = useKeyGenerator();
  const schema = Joi.object({
    serviceType: Joi.string().allow('', null),
    serviceAvailabilityId: Joi.number()
      .required()
      .messages({
        'number.empty': t(`field_is_required`, {
          field: t('service_availability'),
          ns: 'Agents',
        }),
        'number.required': t(`field_is_required`, {
          field: t('service_availability'),
          ns: 'Agents',
        }),
      }),
    description: Joi.string().allow('', null),
    vendorServiceCategoryId: Joi.number()
      .required()
      .messages({
        'number.empty': t(`field_is_required`, {
          field: t('service_category'),
          ns: 'Agents',
        }),
        'number.required': t(`field_is_required`, {
          field: t('service_category'),
          ns: 'Agents',
        }),
      }),
    vendorServiceTagIds: Joi.array(),
  })
    .options({ abortEarly: false, allowUnknown: true })
    .validate(state);
  const onCloseHandler = () => {
    onClose();
    dispatch({ id: 'edit', value: { ...initialState } });
    refresh();
  };
  const clickHandler = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsSubmitted(true);
    try {
      if (schema.error) {
        return showError(t('Shared:please-fix-all-errors'));
      }
      if (activeItem) {
        await UpdateVendorService(
          activeItem.vendorServiceId,
          mapStateToSaveDto(state)
        );
        showSuccess(t('service_updated_successfully'));
      } else {
        await CreateVendorService(mapStateToSaveDto(state));
        showSuccess(t('service_created_successfully'));
      }
      onCloseHandler();
      setIsSubmitted(false);
      reload();
    } catch (error) {
      showError(t('something_went_wrong'));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (activeItem) {
      dispatch({ id: 'edit', value: mapActiveItem(activeItem) });
      refresh();
    }
  }, [activeItem]);

  useEffect(() => {
    refetchCategories();
  }, []);
  
  return (
    <DialogComponent
      isOpen={open}
      onCloseClicked={onClose}
      parentTranslationPath={parentTranslationPath}
      titleText={activeItem ? 'edit_service' : 'add_service'}
      onCancelClicked={onClose}
      onSaveClicked={clickHandler}
      dialogContent={
        <div
          className='childs-wrapper flex flex-wrap gap-1 p-3 fj-center'
          key={key}
        >
          <Spinner isActive={isLoading} />
          <Inputs
            labelValue={'service_type'}
            parentTranslationPath={parentTranslationPath}
            wrapperClasses={'w-75'}
            value={state.serviceType}
            onInputChanged={(event) => {
              dispatch({ id: 'serviceType', value: event.target.value });
            }}
          />
          <LookupAutocomplete
            label={'service_availability'}
            className={'w-75'}
            lookupName={'serviceAvailability'}
            selectedValues={state.selectedAvailability}
            onChange={(_e, value) => {
              dispatch({
                id: 'edit',
                value: {
                  ...state,
                  serviceAvailabilityId: value?.lookupItemId,
                  selectedAvailability: value,
                },
              });
            }}
            error={getErrorByName(schema, 'serviceAvailabilityId').error}
            helperText={getErrorByName(schema, 'serviceAvailabilityId').message}
            withError
            isSubmitted={isSubmitted}
            parentTranslationPath={parentTranslationPath}
          />
          <AutocompleteComponent
            data={categories}
            wrapperClasses={'w-75'}
            labelValue={'service_category'}
            getOptionSelected={(option, value) =>
              option.serviceCategoryId === value.serviceCategoryId
            }
            isSubmitted={isSubmitted}
            parentTranslationPath={parentTranslationPath}
            selectedValues={state.selectedCategory}
            multiple={false}
            displayLabel={(option) => option.serviceCategoryName}
            withoutSearchButton
            onChange={(_e, newValue) => {
              dispatch({
                id: 'edit',
                value: {
                  ...state,
                  vendorServiceCategoryId: newValue?.serviceCategoryId,
                  selectedCategory: newValue,
                },
              });
            }}
            isWithError
            error={getErrorByName(schema, 'vendorServiceCategoryId').error}
            helperText={
              getErrorByName(schema, 'vendorServiceCategoryId').message
            }
          />
          <LookupAutocomplete
            label={'tags'}
            lookupName={'VendorServiceTags'}
            parentTranslationPath={parentTranslationPath}
            className={'w-75'}
            multiple
            selectedValues={state.selectedTags}
            onChange={(_e, value) => {
              dispatch({
                id: 'edit',
                value: {
                  ...state,
                  vendorServiceTagIds: value.map((item) => item.lookupItemId),
                  selectedTags: value,
                },
              });
            }}
          />
          <Inputs
            labelValue={'description'}
            parentTranslationPath={parentTranslationPath}
            multiline
            rows={4}
            value={state.description}
            wrapperClasses={'w-75'}
            onInputChanged={(event) => {
              dispatch({ id: 'description', value: event.target.value });
            }}
          />
        </div>
      }
    />
  );
};
export default ServiceForm;
